import type { MessagesService, MessagesToast } from './index';
import { effect } from '@preact/signals-react';
import { EnumOrderUpdateType } from '@thinkalpha/trading-ws-client/contracts/order.js';
import { t } from 'i18next';
import { BehaviorSubject, Subject } from 'rxjs';
import { inject, injectable, skipAutoDispose } from 'src/features/ioc';
import { enableUnifiedToasts } from 'src/lib/featureFlags/flags/enableUnifiedToasts';
import { messagesStoreRetentionLimit } from 'src/lib/featureFlags/flags/messagesStoreRetentionLimit';
import {
    createConnectedMessage,
    createDisconnectedMessage,
    deriveMessageFromOrder,
    deriveMessageFromOrderUpdate,
} from 'src/routes/widgets/MessagesWidget/hooks/useSubscribeToMessages';
import type { Message, MessagesStatus } from 'src/routes/widgets/MessagesWidget/types';
import type { ReactBindings } from 'src/types/bindings';

const ERROR_MESSAGE_TYPES = new Set<MessagesStatus>(['rejected', 'suspended', EnumOrderUpdateType.cancelRejection]);
const SUCCESS_MESSAGE_TYPES = new Set<MessagesStatus>(['accepted', 'filled']);
const WARNING_MESSAGE_TYPES = new Set<MessagesStatus>(['canceled', 'cancel-pending']);

@injectable()
export class MessagesServiceImpl implements MessagesService, Disposable {
    readonly [skipAutoDispose] = true;
    readonly messagesReceivedSubject$ = new Subject<Message>();

    #messages: Message[] = [];
    #messages$ = new BehaviorSubject<readonly Message[]>(this.#messages);
    #toastSubscribers = new Set<(toast: MessagesToast) => void>();
    #widgetSubscriberCount = 0;
    #tradingToastSettings = new Map<string, boolean>();
    #lastTradingConnected: boolean | null = null;
    #ignoredFirstTradingConnected = false;
    #disposableStack = new DisposableStack();

    constructor(
        @inject('FeatureFlagService') private featureFlagService: ReactBindings['FeatureFlagService'],
        @inject('TradingSocketService') private tradingSocketService: ReactBindings['TradingSocketService'],
        @inject('CurrentUserModel') private currentUserModel: ReactBindings['CurrentUserModel'],
        @inject('Logger') logger: ReactBindings['Logger'],
    ) {
        const log = logger.getSubLogger({ name: 'MessagesService' });

        const legacyIngressSubscription = this.messagesReceivedSubject$.subscribe((message) => {
            this.publish(message);
        });
        this.#disposableStack.defer(() => {
            legacyIngressSubscription.unsubscribe();
        });

        this.#disposableStack.defer(
            effect(() => {
                const retentionLimit = this.featureFlagService.getSignal(messagesStoreRetentionLimit).value;
                if (this.#messages.length <= retentionLimit) {
                    return;
                }

                this.#messages = this.#messages.slice(0, retentionLimit);
                this.#messages$.next(this.#messages);
            }),
        );

        this.#disposableStack.defer(
            effect(() => {
                const connected = this.tradingSocketService.isConnected.value;
                if (this.#lastTradingConnected === null) {
                    this.#lastTradingConnected = connected;
                    if (connected) {
                        this.#ignoredFirstTradingConnected = true;
                        this.#publish(createConnectedMessage('all', t), { publishToast: false });
                    }
                    return;
                }

                if (connected === this.#lastTradingConnected) {
                    return;
                }

                this.#lastTradingConnected = connected;
                if (connected && !this.#ignoredFirstTradingConnected) {
                    this.#ignoredFirstTradingConnected = true;
                    this.#publish(createConnectedMessage('all', t), { publishToast: false });
                    return;
                }

                this.publish(connected ? createConnectedMessage('all', t) : createDisconnectedMessage('all', t));
            }),
        );

        const orderUpdateSubscription = this.tradingSocketService.orderUpdates$.subscribe((orderUpdate) => {
            const message = deriveMessageFromOrderUpdate(orderUpdate, t);
            if (!message) {
                return;
            }

            this.publish({ ...message, source: 'trading', activityUserId: orderUpdate.requestedBy ?? null });
        });
        this.#disposableStack.defer(() => {
            orderUpdateSubscription.unsubscribe();
        });

        const orderSnapshotSubscription = this.tradingSocketService.orderSnapshots$.subscribe((order) => {
            const message = deriveMessageFromOrder(order, t);
            if (!message) {
                return;
            }

            this.publish({ ...message, source: 'trading', activityUserId: order.creatorId ?? null });
        });
        this.#disposableStack.defer(() => {
            orderSnapshotSubscription.unsubscribe();
        });

        if (TRACE) {
            log.trace('MessagesService initialized');
        }
    }

    publish(message: Message): void {
        this.#publish(message, { publishToast: true });
    }

    #publish(message: Message, options: { publishToast: boolean }): void {
        const normalizedMessage: Message = { ...message, source: message.source ?? 'trading' };
        const retentionLimit = this.featureFlagService.getSignal(messagesStoreRetentionLimit).value;
        const existingIndex = this.#messages.findIndex(
            (existingMessage) => existingMessage.id === normalizedMessage.id,
        );
        const nextMessages =
            existingIndex >= 0
                ? [
                      normalizedMessage,
                      ...this.#messages.slice(0, existingIndex),
                      ...this.#messages.slice(existingIndex + 1),
                  ]
                : [normalizedMessage, ...this.#messages];

        this.#messages = nextMessages.slice(0, retentionLimit);
        this.#messages$.next(this.#messages);
        if (options.publishToast && existingIndex < 0) {
            this.#publishToastIfEligible(normalizedMessage);
        }
    }

    subscribeToMessages(callback: (messages: readonly Message[]) => void): () => void {
        this.#widgetSubscriberCount += 1;
        const subscription = this.#messages$.subscribe(callback);
        let cleanedUp = false;

        return () => {
            if (cleanedUp) {
                return;
            }

            cleanedUp = true;
            subscription.unsubscribe();
            this.#widgetSubscriberCount = Math.max(0, this.#widgetSubscriberCount - 1);
        };
    }

    subscribeToToasts(callback: (toast: MessagesToast) => void): () => void {
        this.#toastSubscribers.add(callback);

        return () => {
            this.#toastSubscribers.delete(callback);
        };
    }

    setTradingToastsEnabled(subscriberId: string, enabled: boolean): void {
        this.#tradingToastSettings.set(subscriberId, enabled);
    }

    clearTradingToastsEnabled(subscriberId: string): void {
        this.#tradingToastSettings.delete(subscriberId);
    }

    #publishToastIfEligible(message: Message): void {
        if (!this.featureFlagService.getSignal(enableUnifiedToasts).value) {
            return;
        }

        if (this.#widgetSubscriberCount > 0 || this.#toastSubscribers.size === 0) {
            return;
        }

        if (!this.#shouldShowToast(message)) {
            return;
        }

        const toast = this.#createToast(message);
        for (const subscriber of this.#toastSubscribers) {
            subscriber(toast);
        }
    }

    #shouldShowToast(message: Message): boolean {
        if ((message.source ?? 'trading') === 'trading') {
            const tradingToastsEnabled = Array.from(this.#tradingToastSettings.values()).every((enabled) => enabled);
            if (!tradingToastsEnabled) {
                return false;
            }
        }

        if (!message.activityUserId) {
            return false;
        }

        return message.activityUserId === this.currentUserModel.userId.value;
    }

    #createToast(message: Message): MessagesToast {
        return {
            id: `messages:${message.id}`,
            title:
                message.source === 'locates'
                    ? message.typeLabel
                    : t('trading::messages::trade-toast-title', { typeLabel: message.typeLabel }),
            message: message.message,
            variant: this.#getToastVariant(message),
            source: message.source ?? 'trading',
            sourceKey: `messages:${message.id}`,
        };
    }

    #getToastVariant(message: Message): MessagesToast['variant'] {
        if (ERROR_MESSAGE_TYPES.has(message.type)) {
            return 'error';
        }

        if (WARNING_MESSAGE_TYPES.has(message.type)) {
            return 'warning';
        }

        if (SUCCESS_MESSAGE_TYPES.has(message.type)) {
            return 'success';
        }

        return 'info';
    }

    [Symbol.dispose](): void {
        this.#disposableStack.dispose();
        this.messagesReceivedSubject$.complete();
        this.#messages$.complete();
    }
}
